import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './Services.css';

const Services = () => {
  const [activeService, setActiveService] = useState(0);
  const [visibleElements, setVisibleElements] = useState([]);
  const sectionRef = useRef(null);

  const services = [
    {
      id: 'patient-management',
      title: 'Patient Management System',
      subtitle: 'Complete Patient Care Solution',
      icon: '🏥',
      description: (
        <>
          Comprehensive Patient Management System for Indian hospitals: dedicated 
          {' '}<Link to="/services/opd-management-software" style={{color: 'var(--primary-blue)', fontWeight: 600}}>OPD</Link>{' '} 
          and {' '}<Link to="/services/ipd-management-software" style={{color: 'var(--primary-blue)', fontWeight: 600}}>IPD</Link>{' '} 
          workflows, Electronic Health Records (EHR), appointment scheduling, patient portals, and secure clinical communication — built for modern multi-specialty clinics in India.
        </>
      ),
      plainDescription: 'Comprehensive Patient Management System for Indian hospitals: dedicated OPD and IPD workflows, Electronic Health Records (EHR), appointment scheduling, patient portals, and secure clinical communication — built for modern multi-specialty clinics in India.',
      features: [
        'Electronic Health Records (EHR)',
        'Patient Registration & Demographics',
        'Appointment Scheduling',
        'Medical History Tracking',
        'Patient Portal Access',
        'Insurance & Billing Integration'
      ],
      benefits: [
        'Improved patient care quality',
        'Reduced administrative overhead',
        'Better patient engagement',
        'Streamlined workflows'
      ],
      image: '📋'
    },
    {
      id: 'clinical-management',
      title: 'Clinical Management',
      subtitle: 'Advanced Clinical Workflows',
      icon: '⚕️',
      description: (
        <>
          Advanced Clinical Management software with Clinical Decision Support (CDS), e‑prescribing, integrated 
          {' '}<Link to="/services/lab-management-software" style={{color: 'var(--primary-blue)', fontWeight: 600}}>Laboratory (LIS)</Link>{' '} 
          and radiology modules, comprehensive treatment planning and digital documentation to improve clinical outcomes.
        </>
      ),
      plainDescription: 'Advanced Clinical Management software with Clinical Decision Support (CDS), e‑prescribing, integrated Laboratory (LIS) and radiology modules, comprehensive treatment planning and digital documentation to improve clinical outcomes.',
      features: [
        'Clinical Decision Support System',
        'E-Prescribing & Medication Management',
        'Lab & Radiology Integration',
        'Treatment Planning',
        'Clinical Documentation',
        'Quality Metrics & Reporting'
      ],
      benefits: [
        'Enhanced clinical outcomes',
        'Reduced medical errors',
        'Evidence-based decisions',
        'Improved care coordination'
      ],
      image: '🩺'
    },
    {
      id: 'financial-management',
      title: 'Financial Management',
      subtitle: 'Complete Revenue Cycle',
      icon: '💰',
      description: (
        <>
          Financial Management & medical billing software: automated billing, 
          {' '}<Link to="/features/nabh-medical-billing" style={{color: 'var(--primary-blue)', fontWeight: 600}}>CGHS / NABH workflows</Link>{' '}, 
          insurance claim processing, revenue cycle management and GST-compliant invoices for hospitals across India.
        </>
      ),
      plainDescription: 'Financial Management & medical billing software: automated billing, CGHS/NABH/NON-NABH workflows, insurance claim processing, revenue cycle management and GST-compliant invoices for hospitals across India.',
      features: [
        'Automated Billing System',
        'Insurance Claims Processing',
        'Revenue Cycle Management',
        'Financial Reporting & Analytics',
        'Payment Processing',
        'Compliance Management'
      ],
      benefits: [
        'Increased revenue collection',
        'Reduced claim denials',
        'Improved cash flow',
        'Enhanced financial transparency'
      ],
      image: '💳'
    },
    {
      id: 'analytics-reporting',
      title: 'Analytics & Reporting',
      subtitle: 'Data-Driven Insights',
      icon: '📊',
      description: 'Analytics & Reporting for hospitals: real-time dashboards, clinical quality metrics, financial performance reports, predictive analytics and custom report builders to optimize operations and resource allocation for Indian hospitals.',
      plainDescription: 'Analytics & Reporting for hospitals: real-time dashboards, clinical quality metrics, financial performance reports, predictive analytics and custom report builders to optimize operations and resource allocation for Indian hospitals.',
      features: [
        'Real-time Dashboards',
        'Clinical Quality Metrics',
        'Operational Analytics',
        'Financial Performance Reports',
        'Predictive Analytics',
        'Custom Report Builder'
      ],
      benefits: [
        'Data-driven decision making',
        'Improved operational efficiency',
        'Better resource allocation',
        'Enhanced quality outcomes'
      ],
      image: '📈'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elementId = entry.target.dataset.element;
            setVisibleElements(prev => [...prev, elementId]);
          }
        });
      },
      { threshold: 0.2 }
    );

    const elements = sectionRef.current?.querySelectorAll('[data-element]');
    elements?.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="services" ref={sectionRef}>
      <Helmet>
        <title>Services — Hospital Management System (HMS) India</title>
        <meta name="description" content="Mediway Nextgen services: OPD & IPD patient management, clinical workflows, hospital billing (CGHS/NABH), analytics and integrations for hospitals across India." />
        <meta name="keywords" content="OPD management software India, IPD management system, hospital billing software CGHS, NABH billing software, hospital analytics India" />
        <link rel="canonical" href="https://mediwaynextgen.wayug.com/#services" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'Organization',
                'name': 'Wayug Infotech Solutions Limited',
                'url': 'https://mediwaynextgen.wayug.com'
              },
              ...services.map(s => ({
                '@type': 'Service',
                'name': s.title,
                'description': s.plainDescription,
                'serviceType': s.subtitle,
                'areaServed': { '@type': 'Country', 'name': 'India' },
                'url': `https://mediwaynextgen.wayug.com/#${s.id}`
              })),
              {
                '@type': 'FAQPage',
                'mainEntity': [
                  {
                    '@type': 'Question',
                    'name': 'Does Mediway Nextgen support OPD and IPD workflows?',
                    'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes — our Patient Management System supports OPD & IPD flows, appointment scheduling, patient portals and EHR for hospitals in India.' }
                  },
                  {
                    '@type': 'Question',
                    'name': 'Can I handle CGHS/NABH billing and insurance claims?',
                    'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes — our Financial Management module supports CGHS, NABH-compliant billing, insurance claims processing and GST-compliant invoices.' }
                  },
                  {
                    '@type': 'Question',
                    'name': 'Do you provide analytics and custom reporting?',
                    'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes — real-time dashboards, custom reports and predictive analytics help hospitals improve operations and decision-making.' }
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>
      <div className="container">
        <div 
          className={`section-header ${visibleElements.includes('header') ? 'section-header--visible' : ''}`}
          data-element="header"
        >
          <h2 className="section-title">
            Best HMS India 2025 Features &amp; Modules
          </h2>
          <p className="section-subtitle">
            Optimize workflows with the most secure hospital management software India relies on, offering clinical EMR, diagnostics, and automated NABH billing solutions.
          </p>
        </div>

        {/* SEO: visually hidden service summary for crawlers */}
        <div className="services-summary sr-only" aria-hidden="true">
          {services.map((service) => (
            <article key={service.id} id={service.id}>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <ul>
                {service.features.slice(0, 4).map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </article>
          ))}
        </div>

        <div className="services-container">
          {/* Service Navigation */}
          <div 
            className={`services-nav ${visibleElements.includes('nav') ? 'services-nav--visible' : ''}`}
            data-element="nav"
          >
            {services.map((service, index) => (
              <button
                key={service.id}
                className={`service-nav-item ${activeService === index ? 'service-nav-item--active' : ''}`}
                onClick={() => {
                  setActiveService(index);
                  const el = document.getElementById(service.id);
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  try { window.history.replaceState(null, '', `#${service.id}`); } catch (e) {}
                }}
              >
                <div className="nav-item__icon">
                  <span>{service.icon}</span>
                </div>
                <div className="nav-item__content">
                  <h4 className="nav-item__title">{service.title}</h4>
                  <p className="nav-item__subtitle">{service.subtitle}</p>
                </div>
                <div className="nav-item__arrow">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            ))}
          </div>

          {/* Service Details */}
          <div 
            className={`service-details ${visibleElements.includes('details') ? 'service-details--visible' : ''}`}
            data-element="details"
          >
            <div className="service-content">
              <div className="service-header">
                <div className="service-icon">
                  <span>{services[activeService].image}</span>
                </div>
                <div className="service-info">
                  <h3 className="service-title">{services[activeService].title}</h3>
                  <p className="service-description">{services[activeService].description}</p>
                </div>
              </div>

              <div className="service-body">
                <div className="service-features">
                  <h4>Key Features</h4>
                  <ul className="features-list">
                    {services[activeService].features.map((feature, index) => (
                      <li key={index} className="feature-item">
                        <div className="feature-check">✓</div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="service-benefits">
                  <h4>Benefits</h4>
                  <div className="benefits-grid">
                    {services[activeService].benefits.map((benefit, index) => (
                      <div key={index} className="benefit-card">
                        <div className="benefit-icon">
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="service-actions">
                <Link to="/contact" className="btn btn-primary">
                  Request Demo
                  <svg className="btn-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
                <Link to="/features" className="btn btn-secondary">
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Dedicated Module Landing Pages */}
        <div
          className={`dedicated-modules ${visibleElements.includes('modules') ? 'dedicated-modules--visible' : ''}`}
          data-element="modules"
        >
          <div className="dedicated-modules__header">
            <h3>Explore Dedicated Modules</h3>
            <p>Deep-dive into each clinical module built specifically for Indian hospitals — with workflow guides, feature lists, and compliance details.</p>
          </div>

          <div className="dedicated-modules__grid">
            <div className="module-card">
              <div className="module-card__icon">🏥</div>
              <div className="module-card__body">
                <h4>OPD Management</h4>
                <p>Appointment scheduling, queue management, OPD-specific EHR and patient portal — designed for busy outpatient departments.</p>
                <ul className="module-card__features">
                  <li>Doctor queue &amp; triage</li>
                  <li>SMS &amp; WhatsApp reminders</li>
                  <li>OPD billing integration</li>
                </ul>
              </div>
              <Link to="/services/opd-management-software" className="module-card__link btn btn-primary">
                View OPD Module →
              </Link>
            </div>

            <div className="module-card">
              <div className="module-card__icon">🛏️</div>
              <div className="module-card__body">
                <h4>IPD Management</h4>
                <p>Bed allocation, nursing care plans, admission &amp; discharge workflows and integrated inpatient billing for hospital wards.</p>
                <ul className="module-card__features">
                  <li>Real-time bed occupancy</li>
                  <li>Nursing task lists</li>
                  <li>Discharge summaries</li>
                </ul>
              </div>
              <Link to="/services/ipd-management-software" className="module-card__link btn btn-primary">
                View IPD Module →
              </Link>
            </div>

            <div className="module-card">
              <div className="module-card__icon">🧪</div>
              <div className="module-card__body">
                <h4>Lab Management (LIS)</h4>
                <p>End-to-end laboratory information system — test ordering, barcode printing, instrument integration, and auto PDF reports.</p>
                <ul className="module-card__features">
                  <li>HL7/ASTM instrument link</li>
                  <li>NABL-ready workflows</li>
                  <li>Auto result validation</li>
                </ul>
              </div>
              <Link to="/services/lab-management-software" className="module-card__link btn btn-primary">
                View Lab Module →
              </Link>
            </div>

            <div className="module-card">
              <div className="module-card__icon">💊</div>
              <div className="module-card__body">
                <h4>Pharmacy Management</h4>
                <p>Drug inventory, prescription dispensing, expiry alerts, GST billing and NABH-compliant pharmacy workflows for hospital pharmacies.</p>
                <ul className="module-card__features">
                  <li>Batch &amp; expiry tracking</li>
                  <li>GST &amp; HSN code billing</li>
                  <li>Auto reorder alerts</li>
                </ul>
              </div>
              <Link to="/services/pharmacy-management-software" className="module-card__link btn btn-primary">
                View Pharmacy Module →
              </Link>
            </div>

            <div className="module-card">
              <div className="module-card__icon">🧾</div>
              <div className="module-card__body">
                <h4>NABH &amp; CGHS Billing</h4>
                <p>Integrated TPA insurance claims, automated CGHS package rate lists, and real-time audit logs for seamless NABH compliance.</p>
                <ul className="module-card__features">
                  <li>CGHS/ECHS auto rates</li>
                  <li>TPA Pre-authorization</li>
                  <li>Audit-ready logging</li>
                </ul>
              </div>
              <Link to="/features/nabh-medical-billing" className="module-card__link btn btn-primary">
                View Billing Module →
              </Link>
            </div>

            <div className="module-card">
              <div className="module-card__icon">🩺</div>
              <div className="module-card__body">
                <h4>Small Hospital &amp; Clinic EMR</h4>
                <p>Designed as the premier EMR software small hospital units trust, featuring multi-doctor scheduling, rapid digital RX, and WhatsApp reminders.</p>
                <ul className="module-card__features">
                  <li>WhatsApp reminders</li>
                  <li>1-Min digital EMR prescriptions</li>
                  <li>Daily cash &amp; billing summaries</li>
                </ul>
              </div>
              <Link to="/emr-software-small-hospital" className="module-card__link btn btn-primary">
                View EMR Module →
              </Link>
            </div>

            <div className="module-card">
              <div className="module-card__icon">🏢</div>
              <div className="module-card__body">
                <h4>Enterprise Hospital ERP</h4>
                <p>Complete modular HIS mapping entire IPD wards, emergency rooms, OT rostering, and corporate governance networks.</p>
                <ul className="module-card__features">
                  <li>Multi-campus analytics</li>
                  <li>OT surgical rostering</li>
                  <li>Role-based RBAC guard</li>
                </ul>
              </div>
              <Link to="/solutions/for-multi-specialty-hospitals" className="module-card__link btn btn-primary">
                View Enterprise ERP →
              </Link>
            </div>

            <div className="module-card">
              <div className="module-card__icon">🔬</div>
              <div className="module-card__body">
                <h4>Diagnostic Lab LIS</h4>
                <p>High-volume LIS orchestrator featuring machine interfaces, pathology sign-off, and barcode printing for a modern diagnostic centre software India setup.</p>
                <ul className="module-card__features">
                  <li>NABL-ready L-J quality charts</li>
                  <li>HL7/ASTM machine interfaces</li>
                  <li>Phlebotomy barcode integration</li>
                </ul>
              </div>
              <Link to="/diagnostic-centre-software-india" className="module-card__link btn btn-primary">
                View Lab Module →
              </Link>
            </div>

            <div className="module-card">
              <div className="module-card__icon">📊</div>
              <div className="module-card__body">
                <h4>Nursing Home Software Suite</h4>
                <p>Comprehensive bed layouts, CGHS/TPA package billing, MAR charts, and NABH-compliant logs built as the finest nursing home software India trusts.</p>
                <ul className="module-card__features">
                  <li>Real-time bed occupancy board</li>
                  <li>NABH medical billing &amp; audits</li>
                  <li>Nursing care &amp; MAR charts</li>
                </ul>
              </div>
              <Link to="/nursing-home-software-india" className="module-card__link btn btn-primary">
                View Nursing Suite →
              </Link>
            </div>

            <div className="module-card">
              <div className="module-card__icon">⚡</div>
              <div className="module-card__body">
                <h4>ABHA / ABDM Integration</h4>
                <p>Natively compliant ABDM M1, M2, and M3 features, offering instant ABHA ID generation and secure health locker interfaces for full HMS software with ABHA integration.</p>
                <ul className="module-card__features">
                  <li>Instant 14-digit ABHA IDs</li>
                  <li>FHIR encrypted standard consent</li>
                  <li>ABDM sandbox certification</li>
                </ul>
              </div>
              <Link to="/abha-integration-hospital" className="module-card__link btn btn-primary">
                View ABHA Module →
              </Link>
            </div>
          </div>
        </div>

        {/* Additional Services */}
        <div
          className={`additional-services ${visibleElements.includes('additional') ? 'additional-services--visible' : ''}`}
          data-element="additional"
        >
          <h3>Additional Services</h3>
          <div className="additional-grid">
            <div className="additional-card">
              <div className="additional-icon">🔧</div>
              <h4>Implementation &amp; Training</h4>
              <p>Complete system setup, data migration, and comprehensive staff training</p>
            </div>
            <div className="additional-card">
              <div className="additional-icon">🛠️</div>
              <h4>Ongoing Support</h4>
              <p>24/7 technical support, system maintenance, and regular updates</p>
            </div>
            <div className="additional-card">
              <div className="additional-icon">🔄</div>
              <h4>System Integration</h4>
              <p>Seamless integration with existing hospital systems and third-party tools</p>
            </div>
          </div>
        </div>

      </div>

      <div className="services-background">
        <div className="background-pattern"></div>
      </div>
    </section>
  );
};

export default Services;